/* ─────────────────────────────────────────────────────────────
   SCREEN & CATALYSTS TAB
   GET /api/v1/stocks/{ticker}/screen-catalysts

   Replicates the reference note's Screen & Catalysts panel:

     overall + factor grades  ← factors (+ model_rank of model_universe).
                                `risk` is PRICE risk; `balance_sheet_quality` is
                                the 6th factor and its chip is HIDDEN while null.
     ranked peer screen       ← factor_screen[] — the sector peer set INCLUDING
                                this ticker, rank-ordered best-first. score_scale
                                caveat: "zscore" rows carry comparable dimension
                                values; "legacy_raw" rows show rank + composite
                                only and their dimension cells are BLANKED.
     screen read              ← screen_read (LLM prose, null until seeded)
     fair value               ← fair_value (upside_to_fv is a PERCENT)
     catalyst calendar        ← catalyst_calendar[] — hard earnings date first,
                                then company-stated events from the call
     catalyst cards           ← catalysts.next_earnings + catalysts.latest_sentiment

   Null rule: any field may be null / any list empty. Render what's present,
   hide what's null, never show "N/A".
───────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip as MuiTooltip } from "@mui/material";
import { getScreenCatalysts } from "../../../api/stocks_api";
import { formatCurrency } from "../../../utils/currency";
import {
  SCREEN_FACTOR_ROWS,
  SCREEN_DIMENSIONS,
  isComparableScale,
  zScoreTone,
  fmtZScore,
  catalystSourceLabel,
  resolveBand,
  signalTone,
  ratingTone,
  SENTIMENT_DISCLAIMER,
  fiscalLabel,
  reportTimeLabel,
  fmtDate,
  fmtPct,
  fmtNum,
  fmtRank,
} from "../../../config/earningsNote";
import {
  NotePage, SectionTitle, Card, TileGrid, Banner, Pill, GradeChip,
  Scroller, Table, Th, Td, Tl,
  NoteSkeleton, NoteEmpty, toneColor, NoteMasthead, NoteFooter,
} from "../Widgets/earningsNoteUI";

const ScreenCatalysts = ({ stock }) => {
  const ticker = stock?.ticker;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ error: false, notFound: false });

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setState({ error: false, notFound: false });
      const res = await getScreenCatalysts(ticker);
      if (cancelled) return;
      if (res?.success) setData(res.data);
      else setState({ error: !res?.notFound, notFound: !!res?.notFound });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  if (loading) return <NoteSkeleton blocks={[90, 220, 200]} />;
  if (state.notFound) return <NoteEmpty>{ticker} is not in coverage.</NoteEmpty>;
  if (state.error || !data)
    return <NoteEmpty>Screen &amp; catalyst data is not available for {ticker}.</NoteEmpty>;

  const {
    factors = {},
    fair_value,
    catalysts,
    factor_screen = [],
    catalyst_calendar = [],
    screen_read,
  } = data;

  const self = data.ticker || ticker;
  const overall = factors.overall ?? null;

  /* Hide any factor whose grade hasn't been pushed yet (never render "N/A"). */
  const factorRows = SCREEN_FACTOR_ROWS.map((f) => ({
    ...f,
    ...(factors[f.key] ?? {}),
  })).filter((f) => f.grade != null && f.grade !== "" && f.grade !== "-");

  /* Any legacy-scale row in the peer screen gets the blank-cells footnote. */
  const hasLegacyRows = factor_screen.some((r) => r && !isComparableScale(r));

  /* ── Fair-value tiles ── */
  const fvTiles = fair_value
    ? [
        fair_value.signal
          ? { label: "Signal", value: fair_value.signal, tone: signalTone(fair_value.signal) }
          : null,
        fair_value.fair_value != null
          ? { label: "Fair value", value: formatCurrency(fair_value.fair_value) }
          : null,
        fair_value.upside_to_fv != null
          ? {
              label: "Upside to FV",
              value: fmtPct(fair_value.upside_to_fv, { decimals: 1 }),
              tone: Number(fair_value.upside_to_fv) >= 0 ? "pos" : "neg",
              hint: Number(fair_value.upside_to_fv) < 0 ? "trading above fair value" : "trading below fair value",
            }
          : null,
        fair_value.entry_price != null
          ? { label: "Entry price", value: formatCurrency(fair_value.entry_price), tone: "pos" }
          : null,
        fair_value.exit_price != null
          ? { label: "Exit price", value: formatCurrency(fair_value.exit_price), tone: "neg" }
          : null,
      ].filter(Boolean)
    : [];

  const nextEarnings = catalysts?.next_earnings ?? null;
  const latestSentiment = catalysts?.latest_sentiment ?? null;
  const sentBand = latestSentiment
    ? resolveBand(latestSentiment.band, latestSentiment.composite_score)
    : null;

  const nothing =
    !overall &&
    factorRows.length === 0 &&
    fvTiles.length === 0 &&
    factor_screen.length === 0 &&
    catalyst_calendar.length === 0 &&
    !screen_read &&
    !nextEarnings &&
    !latestSentiment;
  if (nothing)
    return <NoteEmpty>Screen &amp; catalyst data is not available for {ticker}.</NoteEmpty>;

  return (
    <NotePage>
      {/* ── Masthead ── */}
      <NoteMasthead
        eyebrow="Screen & catalysts"
        title={`${stock?.company_name || ticker} (${self}) — Screen & Catalysts`}
        meta="Factor screen from the internal ranking model · quant screen, not a recommendation"
      />

      {/* ── Overall ── */}
      {overall && (
        <Card accent={toneColor(ratingTone(overall.rating))} style={{ marginTop: "var(--spacing-sm)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-lg)", flexWrap: "wrap" }}>
            <div>
              <div
                style={{
                  color: "var(--color-text-muted)",
                  fontSize: "var(--font-size-xs)",
                  fontWeight: "var(--font-weight-semibold)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                Overall
              </div>
              <div
                style={{
                  color: toneColor(ratingTone(overall.rating)),
                  fontSize: "var(--font-size-2xl)",
                  fontWeight: "var(--font-weight-black)",
                  lineHeight: 1.2,
                }}
              >
                {overall.rating ?? "—"}
              </div>
            </div>
            {overall.grade && <GradeChip grade={overall.grade} />}
            {overall.score != null && (
              <div
                style={{
                  color: "var(--color-text-muted)",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                Score {fmtNum(overall.score, 2)}
              </div>
            )}
            {factors.model_rank != null && (
              <div style={{ marginLeft: "auto", textAlign: "right" }}>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Factor-model rank
                </div>
                <div
                  style={{
                    color: "var(--color-text-primary)",
                    fontSize: "var(--font-size-lg)",
                    fontWeight: "var(--font-weight-bold)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {fmtRank(factors.model_rank, factors.model_universe)}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ── Factor screen (self) ── */}
      {factorRows.length > 0 && (
        <>
          <SectionTitle caption="six-factor snapshot · grades are the readable signal">
            Factor screen
          </SectionTitle>
          {/* A card per factor rather than a two-column table: at full width a
              table would strand each grade chip far from its own label. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
              gap: "var(--spacing-sm)",
            }}
          >
            {factorRows.map((f) => (
              <Card key={f.key} style={{ padding: "12px 14px", minWidth: 0 }}>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 8,
                    lineHeight: 1.35,
                  }}
                >
                  {f.label}
                </div>
                <GradeChip grade={f.grade} />
              </Card>
            ))}
          </div>
          <Banner kind="plain" style={{ marginTop: "var(--spacing-sm)" }}>
            Factor scores are model-scale numbers and are not directly comparable across factors, so
            only the grades are shown. Quant screen — not a recommendation.
          </Banner>
        </>
      )}

      {/* ── Ranked peer screen ── */}
      {factor_screen.length > 0 && (
        <>
          <SectionTitle caption={`sector peer set incl. ${self} · rank-ordered, best first`}>
            Where it ranks
          </SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Rank</Th>
                  <Th left>Company</Th>
                  <Th>Composite</Th>
                  {SCREEN_DIMENSIONS.map((d) => (
                    <Th key={d.key}>{d.label}</Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {factor_screen.map((r, i) => {
                  const isSelf = r.ticker === self;
                  const comparable = isComparableScale(r);
                  const rowStyle = isSelf
                    ? { backgroundColor: "var(--color-surface-hover)" }
                    : undefined;
                  return (
                    <tr
                      key={r.ticker ?? i}
                      onClick={() =>
                        !isSelf && r.ticker && navigate(`/stocks/${r.ticker}/details/summary`)
                      }
                      style={{ cursor: isSelf ? "default" : "pointer", ...rowStyle }}
                    >
                      <Tl style={rowStyle}>{fmtRank(r.model_rank, r.model_universe)}</Tl>
                      <Td left style={rowStyle}>
                        <span
                          style={{
                            color: isSelf ? "var(--color-text-primary)" : "var(--color-primary)",
                            fontWeight: isSelf
                              ? "var(--font-weight-bold)"
                              : "var(--font-weight-medium)",
                          }}
                        >
                          {r.company_name ? `${r.company_name} (${r.ticker})` : r.ticker}
                        </span>
                        {!comparable && (
                          <MuiTooltip
                            title="This name is still on the legacy raw factor scale, so its dimension scores are not comparable with the z-scored rows — only its rank and composite are shown."
                            placement="top"
                            arrow
                          >
                            <span style={{ color: "var(--color-warning)", cursor: "help" }}> ⚠</span>
                          </MuiTooltip>
                        )}
                      </Td>
                      <Td bold={isSelf} style={rowStyle}>
                        {fmtNum(r.composite, 2)}
                      </Td>
                      {SCREEN_DIMENSIONS.map((d) => (
                        <Td
                          key={d.key}
                          tone={comparable ? zScoreTone(r[d.key]) : "muted"}
                          style={rowStyle}
                        >
                          {/* legacy_raw rows are on the old scale — blank the cells */}
                          {comparable ? fmtZScore(r[d.key]) : ""}
                        </Td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Scroller>
          <div
            style={{
              marginTop: 6,
              color: "var(--color-text-muted)",
              fontSize: "var(--font-size-xs)",
              lineHeight: 1.6,
            }}
          >
            Composite and rank are comparable across the whole universe. Dimension values are
            z-scores — positive is better, and a blank cell is a name still on the legacy raw scale,
            where the numbers would not mix.
          </div>
        </>
      )}

      {/* ── Screen read ── */}
      {screen_read && (
        <Banner kind="info" style={{ marginTop: "var(--spacing-md)" }}>
          <strong>Screen read: </strong>
          <span style={{ fontWeight: 400 }}>{screen_read}</span>
        </Banner>
      )}

      {/* ── Fair value ── */}
      {fvTiles.length > 0 && (
        <>
          <SectionTitle caption="upside is expressed as a percentage of fair value">
            Fair value
          </SectionTitle>
          <TileGrid tiles={fvTiles} min={150} />

          {(fair_value?.fv_wide || fair_value?.quality_ok === false) && (
            <div style={{ marginTop: "var(--spacing-sm)", display: "grid", gap: "var(--spacing-sm)" }}>
              {fair_value?.fv_wide && (
                <Banner kind="warning">
                  <strong>Wide fair-value band</strong> — the model's inputs disagree, so treat the
                  midpoint as indicative only.
                </Banner>
              )}
              {fair_value?.quality_ok === false && (
                <Banner kind="warning">
                  <strong>Quality gate failed</strong> — value signals for this name carry
                  value-trap risk.
                </Banner>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Catalyst calendar ── */}
      {catalyst_calendar.length > 0 && (
        <>
          <SectionTitle caption="hard earnings date first, then events management stated on the call">
            Catalyst calendar
          </SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>When</Th>
                  <Th left>Catalyst</Th>
                  <Th left>Names</Th>
                  <Th left style={{ minWidth: 240 }}>Why it matters</Th>
                  <Th left>Source</Th>
                </tr>
              </thead>
              <tbody>
                {catalyst_calendar.map((c, i) => {
                  const approx = typeof c.date_label === "string" && c.date_label.includes("~");
                  const hard = /hard date/i.test(c.source ?? "");
                  return (
                    <tr key={`${c.date_label}-${i}`}>
                      <Tl>
                        {c.date_label ?? "—"}
                        {approx && (
                          <MuiTooltip title="Approximate timing — the company gave a period, not a date." placement="top" arrow>
                            <span style={{ color: "var(--color-text-muted)", cursor: "help" }}> ⓘ</span>
                          </MuiTooltip>
                        )}
                      </Tl>
                      <Td left wrap bold>{c.catalyst ?? "—"}</Td>
                      <Td left tone="muted">{c.names ?? "—"}</Td>
                      <Td left wrap tone="muted">{c.why ?? "—"}</Td>
                      <Td left>
                        {c.source ? (
                          <Pill tone={hard ? "pos" : "warn"}>{catalystSourceLabel(c.source)}</Pill>
                        ) : (
                          "—"
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Scroller>
        </>
      )}

      {/* ── Catalysts ── */}
      {(nextEarnings || latestSentiment) && (
        <>
          <SectionTitle>Catalysts</SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "var(--spacing-sm)",
            }}
          >
            {nextEarnings && (
              <Card>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 8,
                  }}
                >
                  Next earnings
                </div>
                <div
                  style={{
                    color: "var(--color-text-primary)",
                    fontSize: "var(--font-size-xl)",
                    fontWeight: "var(--font-weight-bold)",
                  }}
                >
                  {fmtDate(nextEarnings.report_date)}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  {nextEarnings.report_time && (
                    <Pill>{reportTimeLabel(nextEarnings.report_time)}</Pill>
                  )}
                  {nextEarnings.eps_estimate != null && (
                    <Pill>EPS est. {formatCurrency(nextEarnings.eps_estimate)}</Pill>
                  )}
                </div>
              </Card>
            )}

            {latestSentiment && (
              <Card accent={sentBand?.color}>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 8,
                  }}
                >
                  Latest call sentiment ·{" "}
                  {fiscalLabel(latestSentiment.fiscal_year, latestSentiment.fiscal_quarter)}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <span
                    style={{
                      color: sentBand?.color ?? "var(--color-text-primary)",
                      fontSize: "var(--font-size-2xl)",
                      fontWeight: "var(--font-weight-black)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {fmtNum(latestSentiment.composite_score, 1)}
                    <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
                      /100
                    </span>
                  </span>
                  {latestSentiment.band && (
                    <span
                      style={{
                        color: sentBand?.color ?? "var(--color-text-secondary)",
                        fontSize: "var(--font-size-sm)",
                        fontWeight: "var(--font-weight-bold)",
                      }}
                    >
                      {latestSentiment.band}
                    </span>
                  )}
                  {latestSentiment.qoq_delta != null && (
                    <span
                      style={{
                        color: toneColor(Number(latestSentiment.qoq_delta) >= 0 ? "pos" : "neg"),
                        fontSize: "var(--font-size-sm)",
                        fontWeight: "var(--font-weight-semibold)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {Number(latestSentiment.qoq_delta) >= 0 ? "▲" : "▼"}{" "}
                      {fmtNum(Math.abs(Number(latestSentiment.qoq_delta)), 1)} QoQ
                    </span>
                  )}
                </div>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    marginTop: 10,
                    lineHeight: 1.5,
                  }}
                >
                  {SENTIMENT_DISCLAIMER}
                </div>
              </Card>
            )}
          </div>
        </>
      )}

      <NoteFooter>
        Factor grades and ranks come from the internal ranking model and fair value from the nightly
        valuation run. Calendar rows sourced from the earnings calendar are hard dates; rows stated
        on the call are the company's own timing — verify before trading.
        {hasLegacyRows
          ? " Peers still on the legacy factor scale show rank and composite only."
          : ""}{" "}
        Quant screen — not a recommendation. For internal analysis only.
      </NoteFooter>
    </NotePage>
  );
};

export default ScreenCatalysts;
